import React, { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import api from '../services/api';
import { getFileUrl, formatarFechaLocal, convocatoriaCerrada } from '../utils/fileUtils';
import { buildUploadImageCandidates } from '../utils/cleroFotos';
import NoticiaCard from '../components/NoticiaCard';
import NoticiaImage from '../components/NoticiaImage';
import { getImageByIndex } from '../utils/exampleImages';
import TexturePattern from '../components/TexturePattern';
import { OrganizationSchema, WebSiteSchema, WebPageSchema } from '../components/SchemaMarkup';
import AnimatedSection from '../components/AnimatedSection';
import HeroSlider from '../components/HeroSlider';
import Escudo from '../components/Escudo';
import ConvocatoriaPopup from '../components/ConvocatoriaPopup';
import {
  FaCalendarAlt, FaHandshake, FaFileSignature, FaDollarSign, FaChartLine,
  FaShieldAlt, FaFileAlt, FaBook, FaClipboardCheck, FaBalanceScale, FaSitemap,
  FaGavel, FaEnvelope, FaFileContract,
  FaClipboardList, FaProjectDiagram, FaFemale, FaUserClock, FaGraduationCap,
  FaWheelchair, FaGlobeAmericas, FaBriefcase, FaChevronDown, FaUsers, FaNewspaper,
  FaComments, FaFileAlt as FaDocument, FaBuilding, FaCity, FaShoppingCart,
  FaBox, FaArchive, FaLaptopCode, FaDigitalTachograph, FaFlag, FaLandmark,
  FaChurch, FaPray, FaCross, FaHeart, FaHandHoldingHeart, FaBible, FaDove,
  FaChild, FaHome, FaPeopleCarry, FaUserFriends, FaScroll, FaUserTie,
  FaMicrophone, FaWhatsapp, FaArrowRight, FaFilePdf, FaTv, FaImages,
  FaHandsHelping
} from 'react-icons/fa';
import { directorioVicarias } from '../data/parroquiasDirectorio';
import './Home.css';
import '../styles/force-center.css';
import '../styles/EMERGENCY-CENTER-FIX.css';
import '../styles/ULTIMATE-CENTER-FIX.css';
import '../styles/FIX-ACCESO-RAPIDO.css';
import '../styles/FIX-CONTACTO.css';
import '../styles/FIX-NOTICIAS-HOME.css';
import '../styles/FIX-GRUPOS-INTERES.css';
import '../styles/FIX-GRUPOS-GRID-LEFT.css';
import '../styles/FIX-UBICACION.css';
import '../styles/FIX-GACETA.css';
import '../styles/FIX-ENLACES-INTERES.css';
import '../styles/FIX-ENLACES-GRID-SCROLL.css';
import '../styles/FIX-SLIDER-HERO.css';
import '../styles/SLIDER-CACHE-BUSTER.css';
import '../styles/GALERIA-FIX-DEFINITIVO.css';
import '../styles/Home-Convocatorias-Galeria.css';

const Home = () => {
  const { t } = useLanguage();
  const [searchQuery, setSearchQuery] = useState('');
  const [tvLoaded, setTvLoaded] = useState(false);
  // Estadísticas desde directorio de parroquias
  const diocesisStats = React.useMemo(() => {
    const vicarias = directorioVicarias.length;
    const parroquias = directorioVicarias.reduce((acc, v) => acc + (v.parroquias?.length || 0), 0);
    const sacerdotes = new Set();
    directorioVicarias.forEach((v) => {
      (v.parroquias || []).forEach((p) => {
        (p.presbiteros || []).forEach((pr) => {
          if (pr?.nombre?.trim()) sacerdotes.add(pr.nombre.trim());
        });
      });
    });
    return {
      vicarias,
      parroquias,
      centrosEvangelizacion: 4,
      comisionesPastoral: 22,
      sacerdotes: sacerdotes.size,
    };
  }, []);

  const { data: noticias = [] } = useQuery({
    queryKey: ['noticias'],
    queryFn: async () => {
      const response = await api.get('/noticias');
      const data = response.data;
      return Array.isArray(data) ? data : [];
    },
    retry: 2,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    refetchOnReconnect: true,
    staleTime: 3 * 60 * 1000,
  });

  const ultimasNoticias = noticias.slice(0, 3);

  const { data: convocatoriasRaw = [] } = useQuery({
    queryKey: ['convocatorias'],
    queryFn: async () => {
      try {
        const response = await api.get('/convocatorias');
        return response.data || [];
      } catch (error) {
        console.error('Error obteniendo convocatorias:', error);
        return [];
      }
    },
    retry: false,
    staleTime: 5 * 60 * 1000,
  });

  const convocatoriasParaHome = React.useMemo(() => {
    const list = Array.isArray(convocatoriasRaw) ? convocatoriasRaw : [];
    return list
      .filter((c) => c.activa !== false && c.titulo)
      .sort((a, b) => {
        const aDest = a.destacada ? 1 : 0;
        const bDest = b.destacada ? 1 : 0;
        if (bDest !== aDest) return bDest - aDest;
        return new Date(b.fecha_inicio || 0) - new Date(a.fecha_inicio || 0);
      })
      .slice(0, 3);
  }, [convocatoriasRaw]);

  const { data: galeriaItemsRaw = [], isLoading: galeriaLoading } = useQuery({
    queryKey: ['galeria', 'todas', 'todas'],
    queryFn: async () => {
      try {
        const response = await api.get('/galeria');
        const data = response.data;
        if (!Array.isArray(data)) return [];
        return data;
      } catch (err) {
        console.error('Error cargando galería para Home:', err);
        return [];
      }
    },
    retry: 3,
    refetchOnMount: 'always',
    staleTime: 2 * 60 * 1000,
  });

  const galeriaItems = React.useMemo(() => {
    const items = Array.isArray(galeriaItemsRaw) ? [...galeriaItemsRaw] : [];
    return items.sort((a, b) => {
      const da = a.fecha_evento || a.created_at || 0;
      const db = b.fecha_evento || b.created_at || 0;
      return new Date(db) - new Date(da);
    });
  }, [galeriaItemsRaw]);

  // 3 items: priorizar eventos distintos, si no hay 3 distintos tomar los 3 más recientes
  const galeriaItemsParaHome = React.useMemo(() => {
    const getBaseEvent = (titulo) => {
      if (!titulo || typeof titulo !== 'string') return '';
      return titulo.replace(/\s*-\s*Foto\s*\d+\s*$/i, '').trim() || titulo;
    };
    const byEvent = [];
    const seen = new Set();
    for (const item of galeriaItems) {
      const base = getBaseEvent(item.titulo);
      if (!base || seen.has(base)) continue;
      seen.add(base);
      byEvent.push(item);
      if (byEvent.length >= 3) break;
    }
    const result = byEvent.length >= 3 ? byEvent : galeriaItems.slice(0, 3);
    return result;
  }, [galeriaItems]);

  const scrollToNext = () => {
    const nextSection = document.querySelector('.anuncios, .acceso-rapido');
    if (nextSection) {
      nextSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const { data: config = {} } = useQuery({
    queryKey: ['configuracion'],
    queryFn: async () => {
      try {
        const response = await api.get('/configuracion');
        return response.data;
      } catch (error) {
        console.error('Error obteniendo configuración:', error);
        return {};
      }
    },
    retry: false,
    refetchOnWindowFocus: false,
    staleTime: 10 * 60 * 1000,
  });

  const { data: documentos = [] } = useQuery({
    queryKey: ['gaceta'],
    queryFn: async () => {
      try {
        const response = await api.get('/gaceta');
        return response.data;
      } catch (error) {
        console.error('Error obteniendo documentos:', error);
        return [];
      }
    },
    retry: false,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
  });

  const { data: sliderData = [] } = useQuery({
    queryKey: ['slider'],
    queryFn: async () => {
      const response = await api.get('/slider');
      const data = response.data;
      return Array.isArray(data) ? data : [];
    },
    retry: 2,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    staleTime: 3 * 60 * 1000,
  });

  // Slider: lee desde el admin (API) o fallback a imagen por defecto
  const defaultSlide = {
    id: 1,
    title: '',
    subtitle: '',
    image: `${process.env.PUBLIC_URL || ''}/images/slider01.png`,
    imageCandidates: [`${process.env.PUBLIC_URL || ''}/images/slider01.png`],
    link: '/clero',
    backgroundPosition: 'center 0%',
  };
  const sliderSlides = Array.isArray(sliderData) && sliderData.length > 0
    ? sliderData
        .sort((a, b) => (a.orden ?? 0) - (b.orden ?? 0))
        .map((s) => {
          const candidates = s.imagen_url ? buildUploadImageCandidates(s.imagen_url) : [];
          return {
            id: s.id,
            title: s.titulo || '',
            subtitle: s.subtitulo || '',
            image: candidates[0] || (s.imagen_url ? getFileUrl(s.imagen_url) : defaultSlide.image),
            imageCandidates: candidates.length > 0 ? candidates : undefined,
            link: s.link || '',
            backgroundPosition: s.background_position || 'center 0%',
          };
        })
    : [defaultSlide];

  return (
    <div className="home">
      <OrganizationSchema 
        name="Diócesis de Ipiales"
        url={window.location.origin}
        email="diocesisdeipiales@gmail.com"
        telephone="+57 315 466 9018"
      />
      <WebSiteSchema 
        name="Diócesis de Ipiales"
        url={window.location.origin}
        searchUrl={`${window.location.origin}/busqueda?q={search_term_string}`}
      />
      <WebPageSchema url={`${window.location.origin}/`} />
      {/* Hero Slider */}
      <div className="hero-slider-wrapper">
        <HeroSlider slides={sliderSlides} />
      </div>

      {/* CTA único bajo el hero */}
      <section className="hero-cta-bar" aria-label="Accesos rápidos">
        <div className="hero-cta-bar-inner">
          <p className="hero-cta-bar-text">
            En comunión, participación y misión
          </p>
          <div className="hero-cta-bar-actions">
            <Link to="/noticias" className="hero-cta-btn hero-cta-btn--primary">
              Ver noticias
            </Link>
            <Link to="/donaciones" className="hero-cta-btn hero-cta-btn--accent">
              <FaHandHoldingHeart aria-hidden /> Donar
            </Link>
          </div>
        </div>
      </section>

      {/* Noticias - debajo del slider */}
      {ultimasNoticias.length > 0 && (
        <AnimatedSection className="section section-bg-white noticias-home" animationType="fadeInLeft">
          <div className="container">
            <h2 className="section-title">{t('home.noticias')}</h2>
            <div className="noticias-grid noticias-grid--editorial">
              {ultimasNoticias.map((noticia, index) => (
                <NoticiaCard
                  key={noticia.id}
                  noticia={noticia}
                  fallbackImageSrc={getImageByIndex(index, 'gobierno')}
                  className={index === 0 ? 'noticia-card--featured' : 'noticia-card--secondary'}
                >
                  <div className="noticia-content">
                    {noticia.facebook_url && (
                      <span className="noticia-fb-chip" title="Publicación vinculada a Facebook">
                        Facebook
                      </span>
                    )}
                    <span className="noticia-fecha">
                      {new Date(noticia.fecha_publicacion || noticia.creado_en).toLocaleDateString('es-CO')}
                    </span>
                    <h3>{noticia.titulo}</h3>
                    <p>
                      {noticia.resumen ||
                        `${String(noticia.contenido || '').replace(/<[^>]+>/g, '').substring(0, 150)}…`}
                    </p>
                  </div>
                </NoticiaCard>
              ))}
            </div>
            <div className="text-center" style={{ marginTop: '2rem' }}>
              <Link to="/noticias" className="btn btn-secondary">
                Ver todas las noticias
              </Link>
            </div>
          </div>
        </AnimatedSection>
      )}

      {/* Convocatorias - estructura propia, alineada con noticias */}
      {convocatoriasParaHome.length > 0 && (
        <AnimatedSection className="section home-seccion-convocatorias" animationType="fadeInUp">
          <div className="home-seccion-inner">
            <h2 className="home-seccion-titulo">{t('home.convocatorias')}</h2>
            <div className="home-seccion-grid">
              {convocatoriasParaHome.map((convocatoria, index) => {
                const descripcionLimpia = (convocatoria.descripcion || '')
                  .replace(/<[^>]+>/g, ' ')
                  .replace(/\s+/g, ' ')
                  .trim()
                  .substring(0, 150);
                const cerrada = convocatoriaCerrada(convocatoria);
                return (
                  <div key={convocatoria.id} className="home-seccion-card" style={{ position: 'relative' }}>
                    <NoticiaImage
                      src={convocatoria.imagen_url || getImageByIndex(index, 'eventos')}
                      alt={convocatoria.titulo}
                      className="home-seccion-card-imagen"
                    />
                    {cerrada && (
                      <span className="home-seccion-card-badge" style={{ background: '#6c757d', color: '#fff' }}>
                        Cerrada
                      </span>
                    )}
                    {convocatoria.destacada && !cerrada && (
                      <span className="home-seccion-card-badge" style={{ background: '#ffc107', color: '#1a2d42' }}>
                        ★ Destacada
                      </span>
                    )}
                    <div className="home-seccion-card-body">
                      <span className="home-seccion-card-fecha">
                        Cierre: {convocatoria.fecha_fin ? formatarFechaLocal(convocatoria.fecha_fin, { day: 'numeric', month: 'short', year: 'numeric' }) : '-'}
                      </span>
                      <h3>{convocatoria.titulo}</h3>
                      <p>{descripcionLimpia}{descripcionLimpia.length >= 150 ? '...' : ''}</p>
                      <Link to={`/convocatorias/${convocatoria.id}`} className="btn">
                        Ver convocatoria →
                      </Link>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="home-seccion-footer">
              <Link to="/convocatorias" className="btn btn-secondary">
                Ver todas las convocatorias
              </Link>
            </div>
          </div>
        </AnimatedSection>
      )}

      {/* Donaciones - banner destacado estilo Pasto */}
      <AnimatedSection className="section diezmo-section calendario-style-section" animationType="fadeInUp">
        <div className="container">
          <h2 className="section-title">
            {(config?.donaciones_campana_titulo || 'Donaciones')
              .toString()
              .replace(/[\u200B-\u200D\uFEFF]/g, '')
              .trim()
              .toUpperCase()}
          </h2>
          <p className="section-subtitle diezmo-banner-subtitle">
            Una bendición que se comparte. Tu generosidad sostiene la misión y el servicio de nuestra Diócesis.
          </p>
          <div className="diezmo-card">
            <div className="diezmo-content">
              <Link to="/donaciones" className="btn btn-diezmo btn-calendario">
                <FaHandHoldingHeart className="donar-btn-icon" aria-hidden />
                <span className="donar-btn-text">{config?.donaciones_boton_texto || 'Donar ahora'}</span>
                <FaArrowRight className="donar-btn-arrow" aria-hidden />
              </Link>
            </div>
          </div>
        </div>
      </AnimatedSection>

      {/* Galería - estructura propia, alineada con noticias */}
      <AnimatedSection className="section home-seccion-galeria" animationType="fadeInUp">
        <div className="home-seccion-inner">
          <h2 className="home-seccion-titulo">GALERÍA FOTOGRÁFICA DESTELLOS DE FE</h2>
          <p className="home-seccion-subtitulo">
            Explora fotografías y videos de las actividades de nuestra Diócesis
          </p>
          {galeriaLoading ? (
            <div className="home-seccion-loading">
              <FaImages />
              <p>Cargando galería...</p>
            </div>
          ) : galeriaItems.length > 0 ? (
            <div className="home-seccion-grid">
              {galeriaItemsParaHome.map((item) => {
                const imgUrl = item.thumbnail_url || (item.tipo === 'foto' ? item.archivo_url : null);
                const desc = (item.descripcion || '').replace(/<[^>]*>/g, '').trim() || 'Fotografía o video de nuestras actividades';
                const excerpt = desc.substring(0, 120) + (desc.length > 120 ? '...' : '');
                const fecha = item.fecha_evento ? new Date(item.fecha_evento).toLocaleDateString('es-CO') : null;
                return (
                  <Link
                    key={item.id}
                    to={`/galeria?id=${item.id}`}
                    className="home-seccion-card home-seccion-card-link"
                  >
                    {imgUrl ? (
                      <NoticiaImage
                        src={getFileUrl(imgUrl)}
                        alt={item.titulo || 'Galería'}
                        className="home-seccion-card-imagen"
                      />
                    ) : (
                      <div className="home-seccion-card-video-placeholder">
                        <FaImages />
                        <span>Video</span>
                      </div>
                    )}
                    <div className="home-seccion-card-body">
                      {fecha && <span className="home-seccion-card-fecha">{fecha}</span>}
                      <h3>{item.titulo || 'Sin título'}</h3>
                      <p>{excerpt}</p>
                      <span className="btn">Ver en galería →</span>
                    </div>
                  </Link>
                );
              })}
            </div>
          ) : (
            <div className="home-seccion-empty">
              <FaImages />
              <p>Próximamente más fotografías y videos de nuestras actividades.</p>
            </div>
          )}
          <div className="home-seccion-footer">
            <Link to="/galeria" className="btn btn-secondary">
              <FaImages /> Ver galería completa
            </Link>
          </div>
        </div>
      </AnimatedSection>

      {/* TV en vivo + radios (más abajo en la página) */}
      {(() => {
        const raw = config?.tv_ipiales_embed_url;
        const tvUrl = (typeof raw === 'string'
          ? raw.trim()
          : (raw?.valor && typeof raw.valor === 'string' ? raw.valor.trim() : ''))
          || (process.env.REACT_APP_TV_IPIALES_EMBED_URL
            || 'https://server.asilivehd.com/hybrid-stream-video-widget/tvipiales');

        const radioLasLajasUrl = 'https://rcnipiales.com/radiochannel/radio-las-lajas/';
        const radioRumbaUrl = 'https://rcnipiales.com/radiochannel/rumba-stereo-104-7/';
        const tvPosterUrl = `${process.env.PUBLIC_URL || ''}/images/fondotv.png`;

        return (
          <AnimatedSection className="section section-bg-azul home-tv-section" animationType="fadeInUp">
            <div className="container">
              <h2 className="section-title">TV IPIALES EN VIVO</h2>
              <p className="section-subtitle">
                Transmisión en vivo de nuestro canal diocesano
              </p>
              <div className="home-tv-embed-wrapper">
                {!tvLoaded ? (
                  <button
                    type="button"
                    className="home-tv-placeholder"
                    style={{ backgroundImage: `url(${tvPosterUrl})` }}
                    onClick={() => setTvLoaded(true)}
                    aria-label="Reproducir TV Ipiales en vivo"
                  />
                ) : (
                  <iframe
                    src={tvUrl}
                    title="TV Ipiales en Vivo"
                    className="home-tv-iframe"
                    allowFullScreen
                    frameBorder="0"
                    allow="autoplay; fullscreen; picture-in-picture; encrypted-media"
                    loading="lazy"
                  />
                )}
              </div>
              <div className="home-tv-actions">
                <a
                  href="/tv-ipiales-en-vivo"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-calendario"
                >
                  <FaTv /> Abrir en pantalla completa
                </a>
              </div>

              {/* Radios debajo del TV - enlace directo para escuchar en vivo sin iframe problemático */}
              <div className="home-radios-wrapper">
                <h3 className="home-radios-title">Radios en vivo</h3>
                <div className="home-radios-grid">
                  <div className="home-radio-card">
                    <h4>Radio Las Lajas 103.9 FM</h4>
                    <a
                      href={radioLasLajasUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="home-radio-link home-radio-link-solo"
                      aria-label="Abrir Radio Las Lajas en el sitio oficial"
                    >
                      Abrir en el sitio oficial
                    </a>
                  </div>
                  <div className="home-radio-card">
                    <h4>Rumba 104.7 FM</h4>
                    <a
                      href={radioRumbaUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="home-radio-link home-radio-link-solo"
                      aria-label="Abrir Rumba en el sitio oficial"
                    >
                      Abrir en el sitio oficial
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </AnimatedSection>
        );
      })()}

      {/* La Iglesia un lugar seguro */}
      <AnimatedSection className="section section-bg-white proteccion-section" animationType="fadeInUp">
        <div className="container">
          <div className="proteccion-card">
            <div className="proteccion-icon">
              <FaShieldAlt />
            </div>
            <div className="proteccion-content">
              <h2 className="proteccion-title">La IGLESIA un lugar seguro</h2>
              <p className="proteccion-text">Todos somos protectores de los menores y personas vulnerables</p>
              <Link to="/proteccion-menores" className="btn btn-proteccion">
                Más Información <FaArrowRight />
              </Link>
            </div>
          </div>
        </div>
      </AnimatedSection>

      {/* Calendario de Eventos */}
      <AnimatedSection className="section section-bg-azul calendario-section" animationType="fadeInUp">
        <div className="container">
          <h2 className="section-title">CALENDARIO DE EVENTOS</h2>
          <p className="section-subtitle">Conoce los eventos que día a día desde nuestra Diócesis realizamos</p>
          <div className="calendario-links">
            <Link to="/agenda" className="btn btn-calendario">
              <FaCalendarAlt /> Ver calendario de eventos
            </Link>
          </div>
        </div>
      </AnimatedSection>

      {/* (Eliminado) Secciones: “Tres minutos, tres palabras” y “Apoya la labor evangelizadora” */}



      {/* Contacto y Ubicación - Fusionados */}
      <AnimatedSection 
        id="contacto" 
        className="section section-bg-white contacto-ubicacion" 
        animationType="fadeInUp"
      >
        <div className="container">
          <h2 className="section-title">Contacto y Ubicación</h2>
          
          <div className="contacto-ubicacion-grid">
            {/* Columna izquierda: Info de contacto - diseño limpio */}
            <div className="contacto-ubicacion-info">
              <div className="contacto-info-limpio">
                <p><strong>Dirección</strong><br />Cra. 6 No. 7-01, Ipiales, Nariño</p>
                <p><strong>Email</strong><br /><a href="mailto:diocesisdeipiales@gmail.com">diocesisdeipiales@gmail.com</a></p>
                <p><strong>Horarios</strong><br />Lun - Vie: 8:00 AM - 12:00 PM y 2:00 PM - 6:00 PM</p>
              </div>
              
              {/* Botones de acción */}
              <div className="contacto-botones">
                <Link to="/contacto" className="btn btn-contacto">
                  <FaEnvelope /> Enviar Mensaje
                </Link>
              </div>
              <p className="contacto-judicial-nota">
                <strong>Asuntos judiciales:</strong> para notificaciones judiciales utilice únicamente{' '}
                <a href="mailto:oficinajuridicadiocesisipiales@gmail.com">oficinajuridicadiocesisipiales@gmail.com</a>.
              </p>
            </div>
            
            {/* Columna derecha: Mapa */}
            <div className="contacto-ubicacion-mapa">
              <div className="mapa-container">
                <iframe
                  src="https://www.google.com/maps?q=Carrera+6+No.+7-01,+Ipiales,+Nariño,+Colombia&output=embed"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Ubicación Diócesis de Ipiales"
                ></iframe>
              </div>
            </div>
          </div>
        </div>
      </AnimatedSection>

      {/* Estadísticas Diócesis - enlace completo a parroquias */}
      <AnimatedSection className="section section-estadisticas-diocesis" animationType="fadeInUp">
        <div className="container">
          <Link to="/parroquias" className="estadisticas-diocesis-link">
            <div className="estadisticas-diocesis-grid">
              <div className="estadistica-item">
                <div className="estadistica-icono"><FaUsers /></div>
                <div className="estadistica-numero">{diocesisStats.vicarias}</div>
                <div className="estadistica-label">Vicarías</div>
              </div>
              <div className="estadistica-item">
                <div className="estadistica-icono"><FaChurch /></div>
                <div className="estadistica-numero">{diocesisStats.parroquias}</div>
                <div className="estadistica-label">Parroquias</div>
              </div>
              <div className="estadistica-item">
                <div className="estadistica-icono"><FaBook /></div>
                <div className="estadistica-numero">{diocesisStats.centrosEvangelizacion}</div>
                <div className="estadistica-label">Centros de Evangelización</div>
              </div>
              <div className="estadistica-item">
                <div className="estadistica-icono"><FaHandsHelping /></div>
                <div className="estadistica-numero">{diocesisStats.comisionesPastoral}</div>
                <div className="estadistica-label">Comisiones de Pastoral</div>
              </div>
              <div className="estadistica-item">
                <div className="estadistica-icono"><FaCross /></div>
                <div className="estadistica-numero">{diocesisStats.sacerdotes}</div>
                <div className="estadistica-label">Sacerdotes</div>
              </div>
            </div>
          </Link>
        </div>
      </AnimatedSection>

      <ConvocatoriaPopup convocatorias={convocatoriasRaw || []} />
    </div>
  );
};

export default Home;


